/**
 * Collab Invite Pages — which apply step a viewer sees
 * (planning/10_13_collab_invite_swap_at_apply.md §10).
 *
 * Pure, shared by the apply panel and its test. Account-first, like the
 * Content Rewards v2 funnel: an account first (so the creator's email is
 * mapped), then TikTok (so we can swap their showcase), then the application.
 */

export type InviteAccountState = "anonymous" | "unlinked" | "linked";

export interface InviteViewer {
  account: InviteAccountState;
  /** The account's saved registration, while it waits for TikTok. */
  registration: { registrationId: string; connectUrl: string } | null;
  /** Set when the creator came back from TikTok without connecting. */
  tiktokNotice: "not_connected" | null;
  /** The creator just came back from sign-up (`?apply=1`): save the registration once. */
  autoApply: boolean;
}

export const ANONYMOUS_VIEWER: InviteViewer = {
  account: "anonymous",
  registration: null,
  tiktokNotice: null,
  autoApply: false,
};

/**
 * applied          the application exists (swap progress / success screen)
 * create_account   anonymous: explain the two steps, then the creator sign-up
 * save_application signed in, not linked, nothing saved yet: Apply saves it
 * connect_tiktok   signed in, not linked, saved: explain + Connect TikTok + code
 * apply            linked creator: the one-click apply
 */
export type InviteApplyStep =
  | "applied"
  | "create_account"
  | "save_application"
  | "connect_tiktok"
  | "apply";

export function resolveInviteApplyStep(args: {
  viewer: InviteViewer;
  applied: boolean;
  /** A connect URL the apply action just returned (before the loader reloads). */
  pendingConnectUrl?: string | null;
}): InviteApplyStep {
  const { viewer, applied, pendingConnectUrl } = args;
  if (applied) return "applied";
  if (viewer.account === "linked") return "apply";
  if (viewer.account === "anonymous") return "create_account";
  return viewer.registration || pendingConnectUrl ? "connect_tiktok" : "save_application";
}

/** Save the registration automatically only once, right after sign-up. */
export function shouldAutoSaveRegistration(viewer: InviteViewer, applied: boolean): boolean {
  return (
    viewer.autoApply &&
    !applied &&
    viewer.account === "unlinked" &&
    viewer.registration === null
  );
}

/**
 * The creator just came back to the page mid-apply (sign-up, TikTok, the
 * reconnect leg, a TikTok denial). The apply panel holds their result ("You're
 * in", Connect TikTok, the retry notice) but sits at the bottom of a long page,
 * so the page scrolls to it instead of leaving them on the hero.
 */
export function shouldFocusApplyPanel(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get("registered") === "1" ||
    params.get("reconnected") === "1" ||
    params.get("apply") === "1" ||
    params.get("sample") === "requested" ||
    params.has("tiktok")
  );
}

/**
 * A login return to the invite page or its TikTok completion. The login page
 * treats these as creator destinations (creator sign-up, no account-link hop).
 */
export function isCollabInviteReturn(redirectTo: string | null | undefined): boolean {
  if (!redirectTo) return false;
  return (
    redirectTo.startsWith("/collab/") ||
    redirectTo.startsWith("/collab-invite-registration/complete")
  );
}
